import { KeyboardEvent, useState } from 'react';
import {
  formatProductTag,
  normalizeProductTag,
  PRODUCT_TAG_SUGGESTIONS,
} from '../../types/product';
import './ProductTagChips.css';

interface ProductTagChipsProps {
  tags: string[];
  onChange?: (tags: string[]) => void;
  editable?: boolean;
  compact?: boolean;
  maxTags?: number;
}

function ProductTagChips({
  tags,
  onChange,
  editable = false,
  compact = false,
  maxTags = 15,
}: ProductTagChipsProps) {
  const [draft, setDraft] = useState('');

  const addTag = (raw: string) => {
    if (!onChange) return;
    const next = normalizeProductTag(raw);
    if (!next) return;
    if (tags.some((tag) => tag.toLowerCase() === next.toLowerCase())) {
      setDraft('');
      return;
    }
    if (tags.length >= maxTags) return;
    onChange([...tags, next]);
    setDraft('');
  };

  const removeTag = (target: string) => {
    if (!onChange) return;
    onChange(tags.filter((tag) => tag !== target));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',' || event.key === ' ') {
      event.preventDefault();
      addTag(draft);
    } else if (event.key === 'Backspace' && !draft && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  if (!editable && tags.length === 0) {
    return compact ? null : <p className="product-tags__empty">등록된 해시태그 없음</p>;
  }

  return (
    <div className={`product-tags${compact ? ' is-compact' : ''}${editable ? ' is-editable' : ''}`}>
      <div className="product-tags__list">
        {tags.map((tag) => (
          <span key={tag} className="product-tags__chip">
            {formatProductTag(tag)}
            {editable && (
              <button type="button" aria-label={`${tag} 삭제`} onClick={() => removeTag(tag)}>
                ×
              </button>
            )}
          </span>
        ))}
        {editable && (
          <input
            className="product-tags__input"
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/,/g, ''))}
            onKeyDown={handleKeyDown}
            onBlur={() => addTag(draft)}
            placeholder={tags.length === 0 ? '#ks인증 입력 후 Enter' : '추가...'}
            maxLength={40}
          />
        )}
      </div>
      {editable && (
        <div className="product-tags__suggestions">
          {PRODUCT_TAG_SUGGESTIONS.filter(
            (item) => !tags.some((tag) => tag.toLowerCase() === item.toLowerCase())
          ).map((item) => (
            <button key={item} type="button" onClick={() => addTag(item)}>
              {formatProductTag(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProductTagChips;
